import { unwrap } from '@/lib/apiEnvelope'
import { httpClient } from '@/lib/httpClient'
import { UPLOAD_TIMEOUT_MS } from '@/lib/upload'
import type { ApiResponse, Paginated } from '@/types/api'

import type {
  HistoricoAtendimento,
  RespostaPayload,
  SetorAtendimento,
  SolicitacaoDetalhe,
  SolicitacaoListItem,
  SolicitacaoPayload,
  SolicitacoesParams,
  StatusAtendimento,
} from '../types/atendimento'

const BASE = '/atendimento'

const uploadConfig = (payload: { anexos?: unknown[] }) =>
  payload.anexos?.length ? { timeout: UPLOAD_TIMEOUT_MS } : undefined

/** Filtro vazio vira `undefined`, que o axios omite — a API trata ausente como sem filtro. */
function listParams({ page, pageSize, statusId, setorId, busca }: SolicitacoesParams) {
  return {
    page,
    pageSize,
    statusId: statusId || undefined,
    setorId: setorId || undefined,
    busca: busca.trim() || undefined,
  }
}

export const atendimentoApi = {
  setores: async (): Promise<SetorAtendimento[]> =>
    unwrap(await httpClient.get<ApiResponse<SetorAtendimento[]>>(`${BASE}/setores`)),

  status: async (): Promise<StatusAtendimento[]> =>
    unwrap(await httpClient.get<ApiResponse<StatusAtendimento[]>>(`${BASE}/status`)),

  /** Solicitações abertas pelo usuário logado (a API ignora `setorId` aqui). */
  listarAbertas: async (params: SolicitacoesParams): Promise<Paginated<SolicitacaoListItem>> =>
    unwrap(
      await httpClient.get<ApiResponse<Paginated<SolicitacaoListItem>>>(
        `${BASE}/solicitacoes/abertas`,
        { params: { ...listParams(params), setorId: undefined } },
      ),
    ),

  /** Solicitações dos setores de que o usuário logado faz parte. */
  listarRecebidas: async (params: SolicitacoesParams): Promise<Paginated<SolicitacaoListItem>> =>
    unwrap(
      await httpClient.get<ApiResponse<Paginated<SolicitacaoListItem>>>(
        `${BASE}/solicitacoes/recebidas`,
        { params: listParams(params) },
      ),
    ),

  obter: async (id: string): Promise<SolicitacaoDetalhe> =>
    unwrap(await httpClient.get<ApiResponse<SolicitacaoDetalhe>>(`${BASE}/solicitacoes/${id}`)),

  historico: async (id: string): Promise<HistoricoAtendimento[]> =>
    unwrap(
      await httpClient.get<ApiResponse<HistoricoAtendimento[]>>(
        `${BASE}/solicitacoes/${id}/historico`,
      ),
    ),

  criar: async (payload: SolicitacaoPayload): Promise<SolicitacaoDetalhe> =>
    unwrap(
      await httpClient.post<ApiResponse<SolicitacaoDetalhe>>(
        `${BASE}/solicitacoes`,
        payload,
        uploadConfig(payload),
      ),
    ),

  responder: async (id: string, payload: RespostaPayload): Promise<SolicitacaoDetalhe> =>
    unwrap(
      await httpClient.post<ApiResponse<SolicitacaoDetalhe>>(
        `${BASE}/solicitacoes/${id}/respostas`,
        payload,
        uploadConfig(payload),
      ),
    ),
}
